import React, { useEffect, useState } from "react";
import { getDatabase, onValue, ref, get } from "firebase/database";
import "./transport_orders.css";

const TransportOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispatches, setSelectedDispatches] = useState(null);
  const [clientFilter, setClientFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [transportPayments, setTransportPayments] = useState([]);
  const [transportIssues, setTransportIssues] = useState([]);
  const [podsData, setPodsData] = useState({});
  const [invoicesData, setInvoicesData] = useState({});

  useEffect(() => {
    const db = getDatabase();

    const loadAuxiliaryData = async () => {
      try {
        const paymentsSnap = await get(ref(db, "transport_payment"));
        const issuesSnap = await get(ref(db, "transport_issues"));
        const docsSnap = await get(ref(db, "transport_documents"));

        const payments = [];
        const issues = [];
        const podMap = {};
        const invoiceMap = {};

        if (paymentsSnap.exists()) {
          Object.values(paymentsSnap.val() || {}).forEach((userNode) => {
            Object.values(userNode || {}).forEach((item) => {
              payments.push(item);
            });
          });
        }

        if (issuesSnap.exists()) {
          Object.values(issuesSnap.val() || {}).forEach((userNode) => {
            Object.values(userNode || {}).forEach((item) => {
              issues.push(item);
            });
          });
        }

        if (docsSnap.exists()) {
          const docsVal = docsSnap.val() || {};
          Object.entries(docsVal).forEach(([transporterId, transporterDocs]) => {
            Object.entries(transporterDocs || {}).forEach(([orderId, orderDocs]) => {
              if (orderDocs.POD) {
                podMap[orderId] = Object.values(orderDocs.POD);
              }
              if (orderDocs.invoices) {
                invoiceMap[orderId] = Object.values(orderDocs.invoices);
              }
            });
          });
          setPodsData(podMap);
          setInvoicesData(invoiceMap);
        }

        setTransportPayments(payments);
        setTransportIssues(issues);
      } catch (error) {
        console.error("Error loading transport payment/issue data", error);
      }
    };

    loadAuxiliaryData();

    const ordersRef = ref(db, "transport_orders");

    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setOrders([]);
        setLoading(false);
        return;
      }

      const allOrders = [];

      Object.entries(data).forEach(([transporterId, transporterOrders]) => {
        Object.entries(transporterOrders || {}).forEach(([orderId, order]) => {
          allOrders.push({
            id: orderId,
            transporterId,
            ...order,
          });
        });
      });

      allOrders.sort(
        (a, b) =>
          (b.orderCreatedAt || 0) - (a.orderCreatedAt || 0)
      );

      setOrders(allOrders);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const filteredOrders = orders.filter((order) => {
    const clientName = (
      order.clientName ||
      order.companyName ||
      ""
    ).toLowerCase();

    const status = (order.status || "").toLowerCase();

    const matchesClient = !clientFilter || clientName === clientFilter.toLowerCase();
    const matchesStatus = !statusFilter || status === statusFilter.toLowerCase();

    return matchesClient && matchesStatus;
  });

  const clientOptions = [
    ...new Set(
      orders
        .map((order) => order.clientName || order.companyName)
        .filter(Boolean)
    ),
  ];

  const statusOptions = [
    ...new Set(
      orders
        .map((order) => order.status)
        .filter(Boolean)
    ),
  ];

  if (loading) {
    return <div className="requests-loading">Loading transport orders...</div>;
  }

  return (
    <div className="logistics-page" style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h1 style={{ margin: 0 }}>Transport Orders</h1>

        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
          >
            <option value="">All Clients</option>
            {clientOptions.map((client) => (
              <option key={client} value={client}>
                {client}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="transport-orders-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Order ID</th>
              <th>Client</th>
              <th>Quantity</th>
              <th>Route</th>
              <th>Status</th>
              <th>Transporter</th>
              <th>Phone</th>
              <th>Dispatches</th>
              <th>Created</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order, index) => (
              <tr key={order.id}>
                <td>{index + 1}</td>
                <td>{order.orderId || "-"}</td>
                <td>{order.clientName || order.companyName || "Client"}</td>
                <td>
                  {order.quantity} {order.quantityUnit}
                </td>
                <td>
                  {order.from || "-"} → {order.to || "-"}
                </td>
                <td>{order.status || "-"}</td>
                <td>{order.transporterName || "-"}</td>
                <td>{order.transporterPhone || "-"}</td>
                <td>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedDispatches({
                        ...(order.dispatch || {}),
                        orderId: order.orderId,
                      })
                    }
                  >
                    View Dispatches
                  </button>
                </td>
                <td>
                  {new Date(
                    order.orderCreatedAt ||
                      order.createdAt ||
                      Date.now()
                  ).toLocaleDateString()}
                </td>
                <td>
                  {new Date(
                    order.lastUpdated ||
                      order.orderCreatedAt ||
                      Date.now()
                  ).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedDispatches && (
        <div
          className="dispatch-popup-overlay"
          onClick={() => setSelectedDispatches(null)}
        >
          <div
            className="dispatch-popup"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1001,
              maxHeight: "80vh",
              overflowY: "auto",
              width: "90%",
              maxWidth: "1000px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <h3 style={{ margin: 0 }}>
                Dispatch History
              </h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setSelectedDispatches(null)}
                style={{
                  fontSize: "22px",
                  lineHeight: 1,
                  padding: "6px 12px",
                }}
              >
                ×
              </button>
            </div>

            {!selectedDispatches?.items ? (
              <p>
                No dispatches have been assigned to this order yet.
              </p>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "12px",
                    marginBottom: "20px",
                  }}
                >
                  <div>
                    <strong>From</strong>
                    <div>{selectedDispatches.from || "-"}</div>
                  </div>

                  <div>
                    <strong>To</strong>
                    <div>{selectedDispatches.to || "-"}</div>
                  </div>

                  <div>
                    <strong>Order Quantity</strong>
                    <div>{selectedDispatches.orderQuantity || "-"}</div>
                  </div>

                  <div>
                    <strong>Total Load</strong>
                    <div>{selectedDispatches.totalLoad || "-"}</div>
                  </div>

                  <div>
                    <strong>Order ID</strong>
                    <div>{selectedDispatches.orderId || "-"}</div>
                  </div>
                </div>

                <table className="transport-orders-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date & Time</th>
                      <th>Truck Number</th>
                      <th>Driver Number</th>
                      <th>Load</th>
                      <th>Proof of Delivery</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(selectedDispatches.items).map((dispatch, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          {dispatch.createdAt
                            ? new Date(dispatch.createdAt).toLocaleString()
                            : "-"}
                        </td>
                        <td>{dispatch.truckNumber || "-"}</td>
                        <td>{dispatch.driverNumber || "-"}</td>
                        <td>
                          {dispatch.load || "-"} {dispatch.unit || ""}
                        </td>
                       
                        <td>
                          {(() => {
                            const pod = (podsData[selectedDispatches.orderId] || []).find(
                              (p) =>
                                p.truckId === dispatch.truckId ||
                                p.truckNumber === dispatch.truckNumber
                            );
                            return pod ? (
                              <a
                                className="payment-proof-link"
                                href={pod.fileUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View POD
                              </a>
                            ) : (
                              "-"
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {invoicesData[selectedDispatches.orderId]?.length > 0 && (
                  <div style={{ marginTop: "24px" }}>
                    <h4>Invoices</h4>
                    <ul>
                      {invoicesData[selectedDispatches.orderId].map((invoice, index) => (
                        <li key={index}>
                          <a
                            className="payment-proof-link"
                            href={invoice.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {invoice.fileName || `Invoice ${index + 1}`}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportOrders;
